// TASK-042 — feature slice for the per-row enabled toggle. Pure UI
// surface around `useUpdateAlertRuleMutation`; the entity layer owns the
// data path. The toggle is implemented as a styled checkbox so it inherits
// platform a11y semantics without an extra dep.

import type { AlertRuleResponse } from "@community-analytics/shared";

import { useUpdateAlertRuleMutation } from "@entities/alert-rule";

export interface AlertRuleToggleProps {
  /** The active community id; null disables the control. */
  communityId: string | null;
  rule: AlertRuleResponse;
  /** Optional override for the on/off labels (i18n). */
  labels?: { on: string; off: string };
}

const DEFAULT_LABELS = { on: "Enabled", off: "Disabled" } as const;

export function AlertRuleToggle({
  communityId,
  rule,
  labels = DEFAULT_LABELS,
}: AlertRuleToggleProps) {
  const update = useUpdateAlertRuleMutation(communityId);
  const disabled = communityId === null || update.isPending;
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    update.mutate({ ruleId: rule.id, patch: { enabled: e.target.checked } });
  };
  const label = rule.enabled ? labels.on : labels.off;
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-xs">
      <input
        type="checkbox"
        className="h-4 w-4"
        checked={rule.enabled}
        onChange={onChange}
        disabled={disabled}
        aria-label={`Toggle alert rule ${rule.type}`}
      />
      <span className="text-muted-foreground">{label}</span>
    </label>
  );
}
